import React, { useState, useMemo } from 'react';
import AgGridWrapper from '../../UI/AgGrid';
import { PrimaryButton, SecondaryButton } from "../../UI";
import { useUserContext } from '../../../contexts/UserContext';
import { deleteAdobeUsers, updateAdobeUsersMetadata } from '../../../utils/APIs';
import PrimaryModal from '../../UI/Modal/PrimaryModal';
import { toast } from 'react-toastify';
import AddMetadataModal from './AddMetadataModal';
import { useContentfulContext } from '../../../contexts/ContentfulContext';

const AdobeUsersModal = ({ isOpen, onClose, users, setUsers, isLoading, refreshUsers }) => {
    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();
    const modalContent = content?.USER_MANAGEMENT_ADOBE_USERS_MODAL_CONTENT || {};
    const [actionLoading, setActionLoading] = useState(false);
    const [gridApi, setGridApi] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [modifiedData, setModifiedData] = useState({}); // { _id: { metadata: { key: value } } }
    const [errors, setErrors] = useState({});            // { _id: { field: "error msg" } }
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [confirmDeleteCount, setConfirmDeleteCount] = useState('');
    const [isAddMetadataOpen, setIsAddMetadataOpen] = useState(false);
    const [searchText, setSearchText] = useState('');

    const loadingState = isLoading || actionLoading;

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        if (gridApi) {
            gridApi.setQuickFilter(e.target.value);
        }
    };

    // Collect all known metadata keys across all users
    const allMetadataKeys = useMemo(() => {
        const keySet = new Set();
        (users || []).forEach(user => {
            if (user.metadata) {
                const meta = user.metadata instanceof Map 
                    ? Object.fromEntries(user.metadata) 
                    : user.metadata;
                Object.keys(meta).forEach(k => keySet.add(k));
            }
        });
        return Array.from(keySet);
    }, [users]);

    const handleDelete = async () => {
        setActionLoading(true);
        setShowDeleteConfirm(false);
        setConfirmDeleteCount('');
        try {
            const res = await deleteAdobeUsers(userData?.token, userData?.selectedOrganization?.org_token, selectedIds, userData?.defaultAdobeIO?.x_API_Key);
            if (res?.status) {
                toast.success(res.message || modalContent.messages?.delete_success || "Users deleted successfully.");
                refreshUsers();
                
                // Clear any pending edits and errors for the deleted users
                setModifiedData(prev => {
                    const next = { ...prev };
                    selectedIds.forEach(id => delete next[id]);
                    return next;
                });
                setErrors(prev => {
                    const next = { ...prev };
                    selectedIds.forEach(id => delete next[id]);
                    return next;
                });

                setSelectedIds([]);
            } else {
                toast.error(res?.message || modalContent.messages?.delete_failed || "Failed to delete users.");
            }
        } catch (error) {
            console.error("Failed to delete users", error);
            toast.error(error?.message || modalContent.messages?.delete_failed || "Failed to delete users.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleSave = async () => {
        const updateArray = Object.keys(modifiedData).map(id => ({
            id,
            metadata: modifiedData[id].metadata
        }));

        if (!updateArray.length) return;

        setActionLoading(true);
        setErrors({});
        try {
            const res = await updateAdobeUsersMetadata(userData?.token, userData?.selectedOrganization?.org_token, updateArray);
            if (res?.status) {
                toast.success(res.message || modalContent.messages?.save_success || "Metadata saved successfully.");
                setModifiedData({});
                setErrors({});
                refreshUsers();
            } else {
                toast.error(res?.message || modalContent.messages?.save_failed || "Failed to save metadata.");
            }
        } catch (error) {
            console.error("Failed to save metadata updates", error);
            if (error?.errors) {
                toast.error(error?.message || modalContent.messages?.validation_failed || "Validation failed. Please correct the highlighted rows.");
                setErrors(error.errors);
                if (gridApi) gridApi.redrawRows();
            } else {
                toast.error(error?.message || modalContent.messages?.save_failed || "Failed to save changes.");
            }
        } finally {
            setActionLoading(false);
        }
    };

    const getMetaValue = (user, key) => {
        const id = user._id;
        if (modifiedData[id]?.metadata && key in modifiedData[id].metadata) {
            return modifiedData[id].metadata[key];
        }
        const meta = user.metadata instanceof Map
            ? Object.fromEntries(user.metadata)
            : (user.metadata || {});
        return meta[key] || '';
    };

    const columnDefs = useMemo(() => {
        const initialCols = [
            {
                headerName: '',
                checkboxSelection: true,
                headerCheckboxSelection: true,
                width: 50,
                sortable: false,
                filter: false,
                resizable: true
            },
            { headerName: modalContent.table_headers?.email || 'Email', field: 'email', flex: 1, minWidth: 200, resizable: true, filter: true }
        ];

        const trailingCols = [
            {
                headerName: modalContent.table_headers?.source || 'Source', field: 'source', width: 150, resizable: true, filter: true,
                valueFormatter: ({ value }) => {
                    if (!value) return '';
                    return value
                        .split('_')
                        .map(word => {
                            if (word.toLowerCase() === 'api') return 'API';
                            return word.charAt(0).toUpperCase() + word.slice(1);
                        })
                        .join(' ');
                }
            },
            {
                headerName: modalContent.table_headers?.created_at || 'Created At',
                field: 'createdAt',
                width: 120,
                resizable: true,
                filter: true,
                valueFormatter: params => params.value ? new Date(params.value).toLocaleDateString() : ''
            }
        ];

        // Dynamic metadata columns
        const metaCols = allMetadataKeys.map(key => ({
            headerName: key,
            field: `metadata.${key}`,
            width: 160,
            editable: true,
            resizable: true,
            filter: true,
            valueGetter: params => getMetaValue(params.data, key),
            valueSetter: params => {
                const { data, newValue } = params;
                const id = data._id;
                setModifiedData(prev => {
                    const next = { ...prev };
                    const existing = next[id]?.metadata || {};
                    // Get original metadata value
                    const originalMeta = data.metadata instanceof Map
                        ? Object.fromEntries(data.metadata)
                        : (data.metadata || {});
                    const updated = { ...existing, [key]: newValue };

                    // Check if all modified keys match original
                    const isSame = Object.keys(updated).every(k => updated[k] === (originalMeta[k] || ''));
                    if (isSame && Object.keys(next[id]?.metadata || {}).length <= 1) {
                        delete next[id];
                    } else {
                        next[id] = { metadata: updated };
                    }
                    return next;
                });
                // Clear error for this cell if it exists
                if (errors[id]) {
                    setErrors(prev => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                    });
                }
                return true;
            },
            cellClass: params => {
                const id = params.data._id;
                return modifiedData[id]?.metadata && key in modifiedData[id].metadata ? 'bg-amber-50 font-medium' : '';
            },
            cellStyle: params => {
                const id = params.data._id;
                if (errors[id]) return { border: '1px solid #ef4444', backgroundColor: '#fef2f2' };
                return {};
            },
            tooltipValueGetter: params => {
                const id = params.data._id;
                return errors[id] ? Object.values(errors[id]).join(' | ') : null;
            }
        }));

        return [...initialCols, ...metaCols, ...trailingCols];
    }, [allMetadataKeys, modifiedData, errors]);

    const autoSizeStrategy = useMemo(() => ({
        type: "fitGridWidth",
        defaultMinWidth: 100
    }), []);

    const onSelectionChanged = () => {
        if (!gridApi) return;
        const selectedNodes = gridApi.getSelectedNodes();
        setSelectedIds(selectedNodes.map(n => n.data._id));
    };

    const hasChanges = Object.keys(modifiedData).length > 0;

    if (!isOpen) return null;

    return (
        <>
            <PrimaryModal
                hideModal={onClose}
                title={modalContent.title || "Manage Adobe Users"}
                widthClass="max-w-6xl"
            >
                <div className="p-6 max-h-[85vh] overflow-y-auto">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4 w-full">
                        <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
                            <input
                                value={searchText}
                                onChange={handleSearch}
                                className="w-full md:min-w-56 p-2.5 text-sm text-gray-900 bg-transparent rounded-lg border border-gray-200 appearance-none focus:outline-none"
                                placeholder={modalContent.search_placeholder || "Search email..."}
                            />
                        </div>
                        <div className="flex flex-wrap gap-2 items-center w-full md:w-auto justify-end">
                            {loadingState && <div className="text-sm text-gray-500 animate-pulse">{modalContent.processing_text || "Processing..."}</div>}
                            {selectedIds.length > 0 && (
                                <PrimaryButton
                                    handleClick={() => setShowDeleteConfirm(true)}
                                    size="sm"
                                    className="bg-red-600 hover:bg-red-700 whitespace-nowrap"
                                    disable={loadingState}
                                >
                                    {modalContent.delete_btn || "Delete Selected"}
                                </PrimaryButton>
                            )}
                            {hasChanges && (
                                <SecondaryButton
                                    handleClick={handleSave}
                                    size="sm"
                                    className="whitespace-nowrap"
                                    disable={loadingState}
                                >
                                    {modalContent.save_btn || "Save Changes"}
                                </SecondaryButton>
                            )}
                            <PrimaryButton
                                handleClick={() => setIsAddMetadataOpen(true)}
                                size="sm"
                                disable={loadingState}
                                className="whitespace-nowrap"
                            >
                                {modalContent.add_metadata_btn || "Manage User Metadata"}
                            </PrimaryButton>
                        </div>
                    </div>

                    <AgGridWrapper
                        tableId="adobe-users-table"
                        columnDefs={columnDefs}
                        rowData={users}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            resizable: true,
                        }}
                        onGridReady={params => setGridApi(params.api)}
                        onSelectionChanged={onSelectionChanged}
                        rowSelection="multiple"
                        suppressRowClickSelection={true}
                        tooltipShowDelay={300}
                        autoSizeStrategy={autoSizeStrategy}
                        paginationPageSize={content.MANAGE_EXPERIENCE_CONTENT.default_page_size}
                        paginationPageSizeSelector={enums.PAGINATION_PAGE_SIZE_SELECTOR}
                    />

                    {allMetadataKeys.length === 0 && !isLoading && (
                        <p className="text-center text-sm text-gray-400 mt-4" dangerouslySetInnerHTML={{ __html: modalContent.no_metadata_msg || "No metadata columns yet. Click <strong>Manage User Metadata</strong> to define custom fields for users." }} />
                    )}
                </div>
            </PrimaryModal>

            {showDeleteConfirm && (
                <div 
                    tabIndex="-1"
                    aria-hidden="true"
                    className="fixed top-0 left-0 right-0 bottom-0 z-[1060] w-full p-4 overflow-x-hidden overflow-y-auto bg-black-overlay flex justify-center items-center"
                >
                    <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 m-auto">
                        {/* Header with Diagonal Stripes using bg-primary */}
                        <div 
                            className="bg-primary p-8 text-center relative" 
                            style={{ 
                                backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 8px, rgba(255, 255, 255, 0.12) 8px, rgba(255, 255, 255, 0.12) 16px)' 
                            }}
                        >
                            <h3 className="text-2xl font-bold text-white tracking-tight">
                                Delete {selectedIds.length} users?
                            </h3>
                            <button 
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setConfirmDeleteCount('');
                                }}
                                className="absolute top-4 right-4 text-white hover:text-white/80 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-7">
                            <p className="text-[15px] text-[#475467] mb-6 leading-relaxed">
                                You're about to delete {selectedIds.length} users. Use the text field below to confirm the number of users you want to delete.
                            </p>
                            
                            <label className="block text-[13px] font-semibold text-[#344054] mb-2">
                                Number of users to delete
                            </label>
                            
                            <input
                                type="text"
                                value={confirmDeleteCount}
                                onChange={(e) => setConfirmDeleteCount(e.target.value)}
                                className="w-full px-4 py-3.5 text-4xl font-normal text-[#1d2939] bg-white border border-[#d0d5dd] rounded-lg focus:outline-none focus:border-[#475467] focus:ring-1 focus:ring-[#475467] transition-all"
                            />
                        </div>

                        {/* Footer */}
                        <div className="px-7 pb-7 flex gap-3">
                            <button
                                disabled={confirmDeleteCount !== String(selectedIds.length) || loadingState}
                                onClick={handleDelete}
                                className={`px-5 py-3 rounded-lg font-semibold text-sm transition-all flex-1 text-center ${
                                    confirmDeleteCount === String(selectedIds.length) && !loadingState
                                        ? 'bg-primary hover:opacity-90 text-white shadow-sm'
                                        : 'bg-[#f2f4f7] text-[#98a2b3] cursor-not-allowed border border-[#eaecf0]'
                                }`}
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setConfirmDeleteCount('');
                                }}
                                className="px-5 py-3 bg-[#f2f4f7] border border-[#eaecf0] hover:bg-[#e4e7ec] text-[#344054] rounded-lg font-semibold text-sm transition-all flex-1 text-center"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AddMetadataModal
                isOpen={isAddMetadataOpen}
                onClose={() => setIsAddMetadataOpen(false)}
                users={users}
                existingMetadataKeys={allMetadataKeys}
                refreshUsers={refreshUsers}
            />
        </>
    );
};

export default AdobeUsersModal;
